import { useCallback, useContext, useState } from 'react';

import { FilterContextData } from 'contexts/FilterContext';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';
import { downloadCsv, sendNotification } from 'utils/helperMethods';
import { downloadExcel } from 'utils/ppaHelpers';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_PACK_ARCH_EXPORT_CONFIRMED,
    PPA_PACK_ARCH_EXPORT_FAILED,
    PPA_PACK_ARCH_EXPORT_SUCCEEDED,
} from 'utils/mixpanelEventNames';

import type { PricePackBubbleRow, PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import type { PackArchitectureKpiIdMatcher } from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { buildPackArchitectureGlobalFiltersSummary } from './packArchitectureGlobalFilters.helpers';
import { showPackArchitectureExportSuccessNotification } from './packArchitectureExportNotification';
import {
    buildPackArchitectureNormalizedExportRows,
    fetchPackArchitectureExportRows,
    formatPackArchitectureExportRowsForCsv,
    type PackArchitectureExportFormat,
    type PackArchitectureExportScope,
    type PackArchitectureSizeKpiOption,
} from './packArchitectureExport.helpers';

type UsePackArchitectureExportParams = {
    chartTitle: string;
    xAxisLabel: string;
    yAxis: string;
    yAxisLabel: string;
    bubbleRepresentsLabel: string;
    bubbleSizeLabel: string;
    /** Every "Size" (bubble_size) KPI option — drives one export column pair each, automatically. */
    sizeKpiOptions: PackArchitectureSizeKpiOption[];
    showHeatMap: boolean;
    kpiIdMatcher: PackArchitectureKpiIdMatcher;
    rows: PricePackBubbleRow[];
    chartRequest: PricePackArchitectureRequest | null;
    totalCount: number;
    onExportSuccess?: () => void;
};

const buildExportFileName = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 8).replace(/:/g, '-');
    return `price-pack-architecture_${date}_${time}`;
};

const formatExportDateTime = (dateTime: Date) => {
    const month = dateTime.getMonth() + 1;
    const day = dateTime.getDate();
    const year = dateTime.getFullYear();
    const hours24 = String(dateTime.getHours()).padStart(2, '0');
    const minutes = String(dateTime.getMinutes()).padStart(2, '0');
    const seconds = String(dateTime.getSeconds()).padStart(2, '0');

    return `${month}/${day}/${year}  ${hours24}:${minutes}:${seconds}`;
};

const buildExportMetadataRows = ({
    chartTitle,
    xAxisLabel,
    yAxisLabel,
    bubbleRepresentsLabel,
    bubbleSizeLabel,
    showHeatMap,
    globalFiltersValue,
}: {
    chartTitle: string;
    xAxisLabel: string;
    yAxisLabel: string;
    bubbleRepresentsLabel: string;
    bubbleSizeLabel: string;
    showHeatMap: boolean;
    globalFiltersValue: string;
}): Array<Array<string | number>> => {
    const exportDate = formatExportDateTime(new Date());
    const exportedBy = localStorage.getItem('email') ?? '-';

    return [
        ['Global filters', globalFiltersValue || '-'],
        ['X-Axis', xAxisLabel || '-'],
        ['Y-Axis', yAxisLabel || '-'],
        ['Bubble Represents', bubbleRepresentsLabel || '-'],
        ['Bubble Size', bubbleSizeLabel || '-'],
        ['Growth Heatmap', showHeatMap ? 'ON' : 'OFF'],
        [],
        ['Chart Title', chartTitle],
        ['Export Date', exportDate],
        ['Exported By', exportedBy],
    ];
};

/** Base widths for [Segment, X-Axis, Y-Axis, Y-Axis Growth %], then a repeated [value, growth %] pair per Size KPI. */
const buildExportColumnWidths = (sizeKpiCount: number): Array<number | null> => [
    24,
    20,
    16,
    30,
    ...Array<number>(sizeKpiCount).fill(0).flatMap(() => [20, 30]),
];

export const usePackArchitectureExport = ({
    chartTitle,
    xAxisLabel,
    yAxis,
    yAxisLabel,
    bubbleRepresentsLabel,
    bubbleSizeLabel,
    sizeKpiOptions,
    showHeatMap,
    kpiIdMatcher,
    rows,
    chartRequest,
    totalCount,
    onExportSuccess,
}: UsePackArchitectureExportParams) => {
    const {
        allCheckedAttribute,
        timeSelectionLabel,
        timePeriodList,
    } = useContext(FilterContextData);
    const { timePeriodLabel: resolvedTimePeriodLabel } = usePpaQueryFilters();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(
        async ({ dataScope, format }: { dataScope: PackArchitectureExportScope; format: PackArchitectureExportFormat }) => {
            if (!chartRequest) {
                sendNotification('info', 'No data available to export.');
                return;
            }

            trackEventMixpanel(PPA_PACK_ARCH_EXPORT_CONFIRMED, {
                data: dataScope,
                format,
                x_axis: xAxisLabel,
                y_axis: yAxisLabel,
                bubble_represents: bubbleRepresentsLabel,
                bubble_size: bubbleSizeLabel,
            });

            setIsExporting(true);
            const exportStartTime = Date.now();
            try {
                const exportRowsData = await fetchPackArchitectureExportRows({
                    scope: dataScope,
                    chartRequest,
                    currentRows: rows,
                    totalCount,
                });

                if (exportRowsData.length === 0) {
                    sendNotification('info', 'No data available to export.');
                    return;
                }

                const globalFiltersValue = buildPackArchitectureGlobalFiltersSummary({
                    timeSelectionLabel: timeSelectionLabel || resolvedTimePeriodLabel,
                    chartRequest,
                    timePeriodList,
                    allCheckedAttribute,
                });

                const metadataRows = buildExportMetadataRows({
                    chartTitle,
                    xAxisLabel,
                    yAxisLabel,
                    bubbleRepresentsLabel,
                    bubbleSizeLabel,
                    showHeatMap,
                    globalFiltersValue,
                });

                const exportResult = buildPackArchitectureNormalizedExportRows({
                    rows: exportRowsData,
                    yAxis,
                    yAxisLabel,
                    xAxisLabel: xAxisLabel || 'X-Axis',
                    bubbleRepresentsLabel: bubbleRepresentsLabel || 'Segment',
                    matchesKpiId: kpiIdMatcher.matches,
                    sizeKpiOptions,
                    metadataRows,
                });

                const fileName = buildExportFileName();
                const fileNameWithExt = `${fileName}.${format}`;

                let fileSizeKb: number | undefined;
                if (format === 'xlsx') {
                    const fileSizeBytes = await downloadExcel(fileName, exportResult.rows, {
                        sheetName: 'Price Pack Architecture',
                        columnFormats: exportResult.columnFormats,
                        columnWidths: buildExportColumnWidths(sizeKpiOptions.length),
                        dataStartRow: exportResult.headerRowIndex + 1,
                    });
                    fileSizeKb = Math.round((fileSizeBytes / 1024) * 100) / 100;
                } else {
                    const csvRows = formatPackArchitectureExportRowsForCsv(
                        exportResult.rows,
                        exportResult.columnFormats,
                        exportResult.headerRowIndex,
                    );
                    const csvEscape = (value: string | number) => {
                        const str = String(value ?? '');
                        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
                    };
                    const bom = '\uFEFF';
                    const csvContent = csvRows.map((row) => row.map(csvEscape).join(',')).join('\n');
                    const csvBlob = new Blob([bom + csvContent]);
                    fileSizeKb = Math.round((csvBlob.size / 1024) * 100) / 100;
                    downloadCsv([bom + csvContent], fileNameWithExt);
                }

                const exportDurationMs = Date.now() - exportStartTime;
                trackEventMixpanel(PPA_PACK_ARCH_EXPORT_SUCCEEDED, {
                    data: dataScope,
                    format,
                    exported_rows: exportRowsData.length,
                    export_duration_ms: exportDurationMs,
                    ...(fileSizeKb !== undefined ? { file_size_kb: fileSizeKb } : {}),
                });

                showPackArchitectureExportSuccessNotification(dataScope);
                onExportSuccess?.();
            } catch (err: any) {
                trackEventMixpanel(PPA_PACK_ARCH_EXPORT_FAILED, {
                    data: dataScope,
                    format,
                    error_type: err?.name ?? 'UnknownError',
                    error_message: err?.message ?? 'Failed to export data',
                });
                sendNotification('error', 'Failed to export data. Please try again.');
            } finally {
                setIsExporting(false);
            }
        },
        [
            allCheckedAttribute,
            bubbleRepresentsLabel,
            bubbleSizeLabel,
            chartRequest,
            chartTitle,
            kpiIdMatcher.matches,
            resolvedTimePeriodLabel,
            rows,
            showHeatMap,
            sizeKpiOptions,
            timePeriodList,
            timeSelectionLabel,
            totalCount,
            xAxisLabel,
            yAxis,
            yAxisLabel,
            onExportSuccess,
        ],
    );

    return { isExporting, handleExport };
};
